import { io } from "socket.io-client";
import { create } from "zustand";
import constants from "@/config/constants";
import type { SocketStore } from "./types";

/**
 * The app's single connection to the `/clients` namespace.
 *
 * One per app rather than one per screen: a screen-level socket reconnects on every navigation, and
 * a café's dashboard navigates constantly. It is kept in a store so the connection outlives the
 * screen that asked for it — leaving the orders screen leaves its rooms, which is a different and
 * much cheaper thing than hanging up.
 *
 * Identity travels in the handshake, not in a message: the server verifies the token against
 * `client.tokens` before the socket exists, and resolves which rooms the client may hear from the
 * store id. Switching account or store therefore means a new socket, never a re-used one.
 */
const useSocket = create<SocketStore>()((set, get) => ({
    socket: null,
    key: null,
    isConnected: false,

    sync: (token, storeId) => {
        if (!token || !storeId) {
            get().drop();
            return null;
        }

        const key = `${token}:${storeId}`;
        const current = get();
        if (current.socket && current.key === key) return current.socket;

        current.socket?.disconnect();

        /**
         * `constants.url` already ends in the API's `/clients` prefix, which happens to be the
         * namespace name too — but one is a path and the other is a namespace, so the target is
         * rebuilt from the origin rather than relying on them staying the same word.
         */
        const origin = new URL(constants.url).origin;

        const socket = io(`${origin}/clients`, {
            transports: ["websocket"],
            auth: { token, storeId },
        });

        socket.on("connect", () => set({ isConnected: true }));
        socket.on("disconnect", () => set({ isConnected: false }));
        /* A refused handshake is a dead token or a store this client cannot reach. `useFetch` already
           ends the session on the API's 401, so there is nothing to do here but stop trying. */
        socket.on("connect_error", () => set({ isConnected: false }));

        set({ socket, key, isConnected: false });
        return socket;
    },

    drop: () => {
        const { socket } = get();
        socket?.disconnect();
        set({ socket: null, key: null, isConnected: false });
    },
}));

export default useSocket;
